import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {ConfirmationContext} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidatedControl} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {BILLING_STATUS_CAPTIONS} from 'src/modules/profile/account/tabs/license-and-features/constants';
import {
    selectAccountLicense,
} from 'src/modules/profile/account/tabs/license-and-features/services/account-license-features-slice';
import {BillingStatus as BillingStatuses} from 'src/openapi/admin/models/BillingStatus';

import Button from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Textarea} from '@pb/kit-component-textarea-v2';

import {AccountBillingSkeleton} from './account-billing-skeleton';
import {useAccountBilling} from './useAccountBilling';

import type {ConfirmationContextType} from 'src/contexts/confirmation-context';
import type {MessageContextType} from 'src/contexts/message-context';
import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {BillingStatus} from 'src/openapi/admin/models/BillingStatus';


type Props = {
    licenseId: number,
    accountId: number,
};

export const AccountBilling: React.FC<Props> = ({licenseId, accountId}) => {
    const [saveAccountBillingBusy, setSaveAccountBillingBusy] = useState(false);
    const license = useSelector(selectAccountLicense);
    const {canEditAccountBilling} = useAdminAccessRight();
    const {isFutureDate} = useDateTime();

    const {
        isLoading: isAccountBillingLoading,
        loadingError: accountBillingLoadingError,
        isChanged: isChangedAccountBilling,
        data: accountBilling,
        setData: setAccountBilling,
        saveChanges: saveAccountBilling,
        discardChanges: discardAccountBilling,
    } = useAccountBilling({licenseId});

    const {setSuccessMessage, setErrorMessage} = useContext<MessageContextType>(MessageContext);
    const {addChangedChunk, removeChangedChunk} = useContext<ConfirmationContextType>(ConfirmationContext);

    useEffect(() => {
        (isChangedAccountBilling ? addChangedChunk : removeChangedChunk)('account-billing');
    }, [isChangedAccountBilling, addChangedChunk, removeChangedChunk]);

    const onCommentsChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setAccountBilling(billing => ({...billing, note: event.target.value}));
    }, [setAccountBilling]);

    const onCommentsBlur = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        setAccountBilling(billing => ({...billing, note: event.target.value.trim()}));
    }, [setAccountBilling]);

    const onBillingStatusChange = useCallback((status: BillingStatus) => {
        setAccountBilling(billing => ({...billing, status}));
    }, [setAccountBilling]);

    const onSaveBilling = useCallback(async () => {
        const errors: (string | null)[] = [];
        const actions = [
            saveAccountBilling,
        ];

        setSaveAccountBillingBusy(true);
        for (const action of actions) {
            const error = await action();
            if (error) {
                errors.push(error);
            }
        }
        setSaveAccountBillingBusy(false);

        if (errors.length) {
            const error = [...new Set(errors)].join(' ');
            setErrorMessage(error);
        } else {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        }
    }, [saveAccountBilling, setErrorMessage, setSuccessMessage]);

    const editDisabled = useMemo(() => {
        if (!canEditAccountBilling(accountId)) {
            return true;
        }

        if (!license) {
            return true;
        }

        return (!isFutureDate(license.endDate) && !license.isActive);
    }, [license, canEditAccountBilling, accountId, isFutureDate]);

    return (
        <Skeleton shown={isAccountBillingLoading} view={<AccountBillingSkeleton/>}>
            <ServerResponseHandler
                serverError={accountBillingLoadingError}
                accessRight={ACCESS_RIGHTS.LICENSE_SCREEN_VIEW as AccessRight}>
                <FormSection headline="Account Billing">
                    <Group direction="v">
                        <WidthItem width="100%">
                            <Label>Billing Status</Label>
                            <ComboBox
                                disabled={editDisabled}
                                value={accountBilling.status}
                                onChange={onBillingStatusChange}>
                                {[
                                    <ComboboxItem
                                        key={BillingStatuses.IN_GOOD_STANDING}
                                        caption={BILLING_STATUS_CAPTIONS[BillingStatuses.IN_GOOD_STANDING]}
                                        value={BillingStatuses.IN_GOOD_STANDING}
                                    />,
                                    <ComboboxItem
                                        key={BillingStatuses.HARD_LOCK_GENERAL_MESSAGING}
                                        caption={BILLING_STATUS_CAPTIONS[BillingStatuses.HARD_LOCK_GENERAL_MESSAGING]}
                                        value={BillingStatuses.HARD_LOCK_GENERAL_MESSAGING}
                                    />,
                                    <ComboboxItem
                                        key={BillingStatuses.HARD_LOCK_BILLING_SPECIFIC}
                                        caption={BILLING_STATUS_CAPTIONS[BillingStatuses.HARD_LOCK_BILLING_SPECIFIC]}
                                        value={BillingStatuses.HARD_LOCK_BILLING_SPECIFIC}
                                    />,
                                ]}
                            </ComboBox>
                        </WidthItem>
                        <ValidatedControl name="account-billing-comment">
                            {invalid => (
                                <WidthItem width="100%">
                                    <Label>Comments</Label>
                                    <Textarea
                                        color={invalid ? 'ERROR' : 'DEFAULT'}
                                        disabled={editDisabled}
                                        maxLength={100}
                                        value={accountBilling.note}
                                        onChange={onCommentsChange}
                                        onBlur={onCommentsBlur}
                                    />
                                </WidthItem>
                            )}
                        </ValidatedControl>
                        <Group>
                            {!editDisabled && (
                                <Group>
                                    <Button
                                        disabled={!isChangedAccountBilling}
                                        busy={saveAccountBillingBusy}
                                        variant="Type.PRIMARY"
                                        onClick={onSaveBilling}>
                                        Save
                                    </Button>
                                    <Button
                                        disabled={!isChangedAccountBilling}
                                        variant="Type.SECONDARY"
                                        onClick={discardAccountBilling}>
                                        Discard Changes
                                    </Button>
                                </Group>
                            )}
                        </Group>
                    </Group>
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
